import React, { useEffect, useReducer, useState, useId } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useDismiss,
  useRole,
  useClick,
  useInteractions,
  FloatingFocusManager,
} from '@floating-ui/react';
import { formatDistanceToNow } from 'date-fns';
import { Icons } from 'react-toastify';
import { BellIcon, CheckIcon, CheckedIcon, RemoveIcon } from '../Icons';
import { ToastContainerZIndex, useCloseToasts } from './ToastNotifications';
import { useSharedNotificationCenter } from '../contexts/NotificationsContext';

const RemoveButton = ({ onClick }: { onClick: () => void }) => (
  <button title="Remove notification" onClick={onClick}>
    <RemoveIcon />
  </button>
);

const CheckButton = ({ onClick }: { onClick: () => void }) => (
  <button title="Mark as read" onClick={onClick}>
    <CheckIcon />
  </button>
);

const ActionButtons = ({
  read,
  check,
  remove,
}: {
  read: boolean;
  remove: () => void;
  check: () => void;
}) => {
  return (
    <div className="itemButtons">
      {read ? (
        <span title="Marked as read">
          <CheckedIcon />
        </span>
      ) : (
        <CheckButton onClick={check} />
      )}
      <RemoveButton onClick={remove} />
    </div>
  );
};

/**
 * Displays the time elapsed since the notification was created
 */
function TimeTracker({ createdAt }: { createdAt: number }) {
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const createdAtDate = new Date(createdAt);
  const timeAgo = formatDistanceToNow(createdAtDate);
  useEffect(() => {
    // refresh value of `createdAt` every ~ 1 minute
    const intervalTimer = setInterval(() => {
      forceUpdate();
    }, 1000);

    return () => {
      clearInterval(intervalTimer);
    };
  }, []);

  return (
    <div className="timeTracker">
      <span>{timeAgo} ago</span>
    </div>
  );
}

/**
 * Main notification center component
 */
function NotificationCenter() {
  const [showUnreadOnly, toggleFilter] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const { setZIndex } = ToastContainerZIndex();
  const closeToasts = useCloseToasts();
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
      // set z-index to 100 when open, otherwise 100000
      if (open) {
        setZIndex(100);
        closeToasts();
      } else {
        setZIndex(100000);
      }
    },
    middleware: [
      offset(6),
      flip({ fallbackAxisSideDirection: 'end' }),
      shift(),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const headingId = useId();

  const {
    notifications,
    clear,
    markAllAsRead,
    markAsRead,
    remove,
    unreadCount,
  } = useSharedNotificationCenter();

  return (
    <div className="NotificationCenter">
      <div
        className="BellIcon"
        title="Notification Center"
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <BellIcon />
        {unreadCount ? (
          <span>{unreadCount >= 100 ? '99+' : unreadCount}</span>
        ) : null}
      </div>
      {isOpen && (
        <FloatingFocusManager context={context} modal={false}>
          <div
            className="container"
            ref={refs.setFloating}
            style={floatingStyles}
            aria-labelledby={headingId}
            {...getFloatingProps()}
          >
            <div className="header">
              <h4>Notifications</h4>
              <div className="headerCheckbox">
                <strong>Unread only</strong>
                <input
                  title="Toggle unread only"
                  name="unread"
                  type="checkbox"
                  checked={showUnreadOnly}
                  onChange={() => {
                    toggleFilter(!showUnreadOnly);
                  }}
                />
              </div>
            </div>
            <div className="content">
              {notifications.length === 0 ||
              (unreadCount === 0 && showUnreadOnly) ? (
                <h4>No notifications!</h4>
              ) : (
                (showUnreadOnly
                  ? notifications.filter((n) => !n.read)
                  : notifications
                ).map((notification) => {
                  const type = notification.type as keyof typeof Icons;
                  const content = notification.content as string;
                  return (
                    <div className="itemContainer" key={notification.id}>
                      <div className="itemIcon">
                        {Icons[type]({
                          theme: 'light',
                          type: notification.type || 'default',
                        })}
                      </div>
                      <div className="itemContent">
                        <span>{content}</span>
                        <TimeTracker createdAt={notification.createdAt} />
                      </div>
                      <ActionButtons
                        read={notification.read}
                        remove={() => remove(notification.id)}
                        check={() => markAsRead(notification.id)}
                      />
                    </div>
                  );
                })
              )}
            </div>
            <div className="header">
              <button style={{ borderRadius: '30px' }} onClick={clear}>
                Clear All
              </button>
              <button style={{ borderRadius: '30px' }} onClick={markAllAsRead}>
                Mark All as read
              </button>
            </div>
          </div>
        </FloatingFocusManager>
      )}
    </div>
  );
}

export default NotificationCenter;
